/**
 * Hero — 인증이지 메인 히어로 (new design)
 *
 * preview/index.html 의 .hero 1:1 매칭
 *  - 데스크톱: 시안 좌표 그대로 (좌측 텍스트 / 우측 큰 사진 / 하단 탭 + 통계)
 *  - 1023 이하: 흐름 레이아웃으로 전환 (CSS에서 처리)
 *
 * 탭 클릭 시 우측 사진을 해당 서비스 이미지로 스왑.
 *  [FIX C03] 로딩 전략 변경 — LCP 개선
 *   - 이전: 5장 전부 eager <img> + new Image() preload → 동일 리소스 2회 요청, 8.6MB
 *   - 현재: 첫 장만 즉시 로드(fetchPriority high). 나머지는 브라우저가 한가할 때
 *           (requestIdleCallback) 또는 탭 클릭 시점에 렌더 → 첫 화면 비용 최소화
 *   - 이미지는 WebP로 교체됨 (8.63MB → 369KB)
 *
 * 데이터: SERVICE_LIST (config/constants.ts)
 */
import { useEffect, useState } from 'react';
import { SERVICE_LIST, HERO_STAT } from '@/config/constants';

export interface HeroProps {
  /** 'CTA 상담신청' 클릭 → contact 폼으로 이동 */
  onCTA: () => void;
}

export function Hero({ onCTA }: HeroProps) {
  const [activeIdx, setActiveIdx] = useState(0);

  // 실제로 DOM에 렌더할 이미지 인덱스. 첫 장은 항상 포함.
  const [mounted, setMounted] = useState<number[]>([0]);

  // 브라우저가 한가해지면 나머지 4장도 렌더 → 탭 전환 시 깜빡임 없음.
  // 첫 페인트를 막지 않으므로 LCP에 영향 없음.
  useEffect(() => {
    const all = SERVICE_LIST.map((_, i) => i);
    const ric = (window as Window & {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    });
    if (ric.requestIdleCallback) {
      const id = ric.requestIdleCallback(() => setMounted(all), { timeout: 4000 });
      return () => ric.cancelIdleCallback?.(id);
    }
    const t = window.setTimeout(() => setMounted(all), 2500);
    return () => window.clearTimeout(t);
  }, []);

  // 탭 클릭 — idle 프리페치보다 먼저 눌러도 해당 이미지를 즉시 렌더
  const selectTab = (idx: number) => {
    setMounted((prev) => (prev.includes(idx) ? prev : [...prev, idx]));
    setActiveIdx(idx);
  };

  return (
    <section className="hero" aria-label="메인 히어로">
      <div className="hero-inner">

        {/* 좌측 헤드라인 */}
        <div className="hero-left">
          <h1>
            <span className="line"><span className="accent">인증이지</span>와 함께</span>
            <span className="line">인증부터 자금까지</span>
            <span className="line">기업 성장의</span>
            <span className="line">모든 혜택을</span>
          </h1>
          <p className="hero-lead">
            경영의 본질에 집중할 수 있도록 전략과 실행을 함께합니다.
          </p>
        </div>

        {/* 좌측 배경 그래프 (장식, 데스크톱에서만 노출) */}
        <div className="hero-graph" aria-hidden="true">
          <img src="/hero-graph.webp" alt="" loading="lazy" decoding="async" />
        </div>

        {/* 우측 큰 사진 (탭으로 스왑)
            - 5장 모두 항상 DOM에 렌더, active만 opacity:1
            - 한 번 디코딩되면 그 후 탭 클릭 시 깜빡임 없음 (단순 opacity 토글) */}
        <div className="hero-photo">
          {SERVICE_LIST.map((svc, idx) =>
            mounted.includes(idx) ? (
              <img
                key={svc.id}
                src={svc.heroImg}
                alt={svc.label}
                width={1200}
                height={900}
                loading={idx === 0 ? 'eager' : 'lazy'}
                // @ts-expect-error fetchPriority는 React 18 타입에 아직 없음
                fetchpriority={idx === 0 ? 'high' : 'low'}
                decoding="async"
                className={`hero-photo-img${idx === activeIdx ? ' active' : ''}`}
              />
            ) : null
          )}
        </div>

        {/* 우측 사진 아래 통계 박스 */}
        <div className="hero-stat">
          <svg className="hero-stat-icon" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="32" cy="32" r="28" fill="none" stroke="#fff" strokeWidth="2.5"/>
            <circle cx="32" cy="32" r="18" fill="none" stroke="#fff" strokeWidth="2.5"/>
            <circle cx="32" cy="32" r="8" fill="none" stroke="#fff" strokeWidth="2.5"/>
            <circle cx="32" cy="32" r="2" fill="#fff"/>
            <path d="M32 32 L52 12" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/>
            <path d="M48 12 L52 12 L52 16" stroke="#fff" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <div className="hero-stat-text">
            <strong>{HERO_STAT.title}</strong><br />
            {HERO_STAT.subtitle}
          </div>
        </div>

        {/* 좌측 하단 5개 탭 */}
        <div className="hero-tabs" role="tablist" aria-label="서비스 선택">
          {SERVICE_LIST.map((svc, idx) => {
            const isActive = idx === activeIdx;
            return (
              <button
                key={svc.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`hero-tab${isActive ? ' active' : ''}`}
                onClick={() => selectTab(idx)}
              >
                {svc.label}
              </button>
            );
          })}
        </div>

        {/* 좌측 CTA 2개 */}
        <div className="hero-cta-row">
          <button type="button" className="cta cta-primary" onClick={onCTA}>
            상담신청하기 →
          </button>
          <a href="#services" className="cta cta-secondary">
            서비스 보기
          </a>
        </div>

      </div>
    </section>
  );
}
